import { type ComponentPropsWithoutRef, type ReactElement } from 'react'

import { isEnumMember } from '@lib/utils/strings'

import { IconSprites } from '@constants/enums/sprites'

export interface IconProps extends ComponentPropsWithoutRef<'svg'> {
  variant: IconSprites
}

const iconSizes: Record<IconProps['variant'], { width: number; height: number }> = {
  [IconSprites.ArrowDown]: {
    width: 10,
    height: 10,
  },
  [IconSprites.BannerArrow]: {
    width: 16,
    height: 12,
  },
  [IconSprites.OpenNew]: {
    width: 11,
    height: 11,
  },
  [IconSprites.Menu]: {
    width: 42,
    height: 42,
  },
  [IconSprites.Close]: {
    width: 42,
    height: 42,
  },
  [IconSprites.ThinArrowDown]: {
    width: 12,
    height: 7,
  },
  [IconSprites.ArrowRight]: {
    width: 20,
    height: 18,
  },
  [IconSprites.SuperhumanMailWithBg]: {
    height: 23,
    width: 23,
  },
  [IconSprites.GrammarlyBubble]: {
    height: 22,
    width: 22,
  },
  [IconSprites.CodaLogo]: {
    height: 19,
    width: 18,
  },
  [IconSprites.GoLogo]: {
    height: 23,
    width: 17,
  },
  [IconSprites.GrammarlyLogo]: {
    width: 19,
    height: 18,
  },
  [IconSprites.Grammarly]: {
    width: 25,
    height: 24,
  },
  [IconSprites.Coda]: {
    width: 25,
    height: 24,
  },
  [IconSprites.Go]: {
    width: 25,
    height: 24,
  },
  [IconSprites.Mail]: {
    width: 25,
    height: 24,
  },
  [IconSprites.HeroOutline]: {
    width: 22,
    height: 22,
  },
  [IconSprites.ReaderReactions]: {
    width: 18,
    height: 19,
  },
  [IconSprites.Sparkle]: {
    width: 17,
    height: 17,
  },
  [IconSprites.Humanizer]: {
    width: 22,
    height: 26,
  },
  [IconSprites.CitationFinder]: {
    width: 22,
    height: 20,
  },
  [IconSprites.Calendar]: {
    width: 21,
    height: 23,
  },
  [IconSprites.Recording]: {
    width: 11,
    height: 8,
  },
  [IconSprites.Send]: {
    width: 15,
    height: 15,
  },
  [IconSprites.Hero]: {
    width: 17,
    height: 24,
  },
  [IconSprites.Home]: {
    width: 16,
    height: 16,
  },
  [IconSprites.Comment]: {
    width: 15,
    height: 13,
  },
  [IconSprites.Gear]: {
    width: 14,
    height: 14,
  },
  [IconSprites.Font]: {
    width: 15,
    height: 16,
  },
  [IconSprites.Bold]: {
    width: 15,
    height: 16,
  },
  [IconSprites.Italic]: {
    width: 16,
    height: 16,
  },
  [IconSprites.Underline]: {
    width: 15,
    height: 16,
  },
  [IconSprites.Strikethrough]: {
    width: 15,
    height: 16,
  },
  [IconSprites.AlignStart]: {
    width: 16,
    height: 16,
  },
  [IconSprites.Info]: {
    width: 16,
    height: 17,
  },
  [IconSprites.CheckMark]: {
    width: 24,
    height: 24,
  },
  [IconSprites.LinkedIn]: {
    width: 13,
    height: 13,
  },
  [IconSprites.X]: {
    width: 13,
    height: 12,
  },
  [IconSprites.TikTok]: {
    width: 14,
    height: 14,
  },
  [IconSprites.Instagram]: {
    width: 14,
    height: 14,
  },
  [IconSprites.Threads]: {
    width: 14,
    height: 14,
  },
  [IconSprites.Facebook]: {
    width: 14,
    height: 14,
  },
}

const isValidSprite = (key: string) => isEnumMember(key, IconSprites)

export function Icon({ variant, ...rest }: IconProps): ReactElement {
  const height = iconSizes?.[variant]?.height ?? 0
  const width = iconSizes?.[variant]?.width ?? 0

  if (!isValidSprite(variant)) {
    console.log(`Invalid sprite key (${variant}) passed to icon.`)
    return <></>
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} {...rest}>
      <use href={`#${variant}`} />
    </svg>
  )
}
